import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, PROVIDER_META, STACK_META } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Sparkles, Trash2, Settings as SettingsIcon, ArrowRight, Clock, Layers, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [stack, setStack] = useState("static");
  const [provider, setProvider] = useState("anthropic");
  const [model, setModel] = useState(PROVIDER_META.anthropic.models[0]);
  const [keyMode, setKeyMode] = useState("universal");
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const load = async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) {
      if (!e?.response) toast.error("Serverə qoşulmaq olmur — backend/URL/CORS yoxla");
      else toast.error("Layihələr yüklənmədi (" + e.response.status + ")");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onProviderChange = (p) => {
    setProvider(p);
    setModel(PROVIDER_META[p].models[0]);
  };

  const createProject = async () => {
    if (!name.trim()) return toast.error("Layihə adı daxil edin");
    setCreating(true);
    try {
      const { data } = await api.post("/projects", { name: name.trim(), provider, model, key_mode: keyMode, stack });
      toast.success("Layihə yaradıldı");
      setOpen(false);
      setName("");
      navigate(`/builder/${data.id}`);
    } catch (e) {
      if (!e?.response) {
        toast.error("Serverə qoşulmaq olmur — backend işləmir, URL və ya CORS yanlışdır");
      } else {
        const detail = e.response.data?.detail;
        toast.error(`Yaradıla bilmədi (${e.response.status})${detail ? ": " + detail : ""}`);
      }
    } finally {
      setCreating(false);
    }
  };

  const deleteProject = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      setProjects((p) => p.filter((x) => x.id !== id));
      toast.success("Silindi");
    } catch (e) {
      toast.error("Silinmədi");
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="sticky top-0 z-50 border-b border-[#222] bg-black/60 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo">
            <div className="w-7 h-7 bg-cyan-500 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-black" />
            </div>
            <span className="font-heading font-black text-lg tracking-tighter">FORGE<span className="text-cyan-500">.AI</span></span>
          </Link>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-xs text-zinc-500 font-mono mr-1" data-testid="user-email">{user?.email}</span>
            <Link to="/settings" data-testid="nav-settings">
              <Button variant="ghost" size="sm" className="rounded-sm text-zinc-400 hover:text-white">
                <SettingsIcon className="w-4 h-4 mr-2" /> Ayarlar
              </Button>
            </Link>
            <Button onClick={() => { logout(); navigate("/login"); }} variant="ghost" size="sm" className="rounded-sm text-zinc-400 hover:text-red-400" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Çıxış
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-500 mb-2">Layihələr</p>
            <h1 className="font-heading font-black text-4xl tracking-tighter">Panel</h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm bg-cyan-500 text-black hover:bg-cyan-400 transition-colors font-medium" data-testid="new-project-btn">
                <Plus className="w-4 h-4 mr-2" /> Yeni Layihə
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0a0a0a] border-[#222] text-white rounded-sm">
              <DialogHeader>
                <DialogTitle className="font-heading font-black tracking-tight">Yeni Layihə</DialogTitle>
              </DialogHeader>
              <div className="space-y-5 py-2">
                <div className="space-y-2">
                  <Label className="text-xs text-zinc-400">Ad</Label>
                  <Input
                    value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="məs. Portfolio saytı"
                    className="rounded-sm bg-transparent border-[#333] focus:ring-1 focus:ring-cyan-500"
                    data-testid="project-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-zinc-400">Stack (Texnologiya)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(STACK_META).map(([key, meta]) => (
                      <button
                        key={key} onClick={() => setStack(key)} type="button"
                        data-testid={`stack-${key}`}
                        className={`border p-3 text-left rounded-sm transition-colors ${stack === key ? "border-cyan-500 bg-cyan-500/5" : "border-[#333] hover:border-[#555]"}`}
                      >
                        <span className="block text-sm font-medium">{meta.label}</span>
                        <span className="block font-mono text-[10px] text-zinc-500">{meta.sub}</span>
                        <span className="block text-[11px] text-zinc-600 mt-1">{meta.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-zinc-400">AI Provayder</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(PROVIDER_META).map(([key, meta]) => (
                      <button
                        key={key} onClick={() => onProviderChange(key)} type="button"
                        data-testid={`provider-${key}`}
                        className={`border p-3 text-left rounded-sm transition-colors ${provider === key ? "border-cyan-500 bg-cyan-500/5" : "border-[#333] hover:border-[#555]"}`}
                      >
                        <span className="block w-2.5 h-2.5 rounded-full mb-2" style={{ background: meta.color }} />
                        <span className="block text-sm font-medium">{meta.label}</span>
                        <span className="block font-mono text-[10px] text-zinc-500">{meta.sub}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-zinc-400">Model</Label>
                  <Select value={model} onValueChange={setModel}>
                    <SelectTrigger className="rounded-sm bg-transparent border-[#333]" data-testid="model-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#333] text-white">
                      {PROVIDER_META[provider].models.map((m) => (
                        <SelectItem key={m} value={m} className="font-mono text-xs">{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-zinc-400">Açar rejimi</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button" onClick={() => setKeyMode("universal")}
                      data-testid="key-mode-universal"
                      className={`border p-3 text-left rounded-sm transition-colors ${keyMode === "universal" ? "border-cyan-500 bg-cyan-500/5" : "border-[#333] hover:border-[#555]"}`}
                    >
                      <span className="block text-sm font-medium">Universal Açar</span>
                      <span className="block text-[11px] text-zinc-500">Emergent (hazır)</span>
                    </button>
                    <button
                      type="button" onClick={() => setKeyMode("own")}
                      data-testid="key-mode-own"
                      className={`border p-3 text-left rounded-sm transition-colors ${keyMode === "own" ? "border-cyan-500 bg-cyan-500/5" : "border-[#333] hover:border-[#555]"}`}
                    >
                      <span className="block text-sm font-medium">Öz API Açarım</span>
                      <span className="block text-[11px] text-zinc-500">Ayarlardan əlavə et</span>
                    </button>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={createProject} disabled={creating}
                  className="rounded-sm bg-cyan-500 text-black hover:bg-cyan-400 transition-colors font-medium w-full"
                  data-testid="create-project-submit"
                >
                  {creating ? "Yaradılır..." : "Yarat və Başla"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-zinc-500 font-mono text-sm">Yüklənir...</div>
        ) : projects.length === 0 ? (
          <div className="border border-[#222] border-dashed p-20 text-center" data-testid="empty-state">
            <Layers className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-400 mb-1">Hələ layihə yoxdur</p>
            <p className="text-sm text-zinc-600">İlk AI saytını yaratmaq üçün "Yeni Layihə" düyməsinə bas.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-[#222]">
            {projects.map((p) => (
              <div key={p.id} className="border-r border-b border-[#222] p-6 hover:bg-[#0a0a0a] transition-colors group relative" data-testid={`project-card-${p.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: PROVIDER_META[p.provider]?.color || "#888" }} />
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="text-zinc-600 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100" data-testid={`delete-${p.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[#0a0a0a] border-[#222] text-white rounded-sm">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Layihəni sil?</AlertDialogTitle>
                        <AlertDialogDescription className="text-zinc-500">"{p.name}" birdəfəlik silinəcək.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-sm bg-transparent border-[#333] hover:bg-[#1a1a1a]">Ləğv et</AlertDialogCancel>
                        <AlertDialogAction onClick={() => deleteProject(p.id)} className="rounded-sm bg-red-500 hover:bg-red-600 text-white" data-testid={`confirm-delete-${p.id}`}>Sil</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
                <Link to={`/builder/${p.id}`} className="block">
                  <h3 className="text-lg font-semibold mb-2 group-hover:text-cyan-500 transition-colors">{p.name}</h3>
                  <div className="flex items-center gap-3 mb-6">
                    <span className="font-mono text-[10px] uppercase tracking-widest border border-[#333] px-2 py-0.5 text-zinc-400">{STACK_META[p.stack]?.label || "Statik"}</span>
                    <span className="font-mono text-[10px] uppercase tracking-widest border border-[#333] px-2 py-0.5 text-zinc-400">{PROVIDER_META[p.provider]?.label}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-xs text-zinc-600">
                      <Clock className="w-3 h-3" /> {new Date(p.updated_at).toLocaleDateString()}
                    </span>
                    <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-cyan-500 group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
